import type { FeedbackCategory, FeedbackItem, FeedbackStatus, FeedbackSubmitter } from '../index.js'
import { FEEDBACK_CATEGORY_CLASS, feedbackCategoryOption } from './feedbackCategories.js'

const STATUS_LABEL: Record<FeedbackStatus, string> = { open: 'Open', completed: 'Completed', discarded: 'Discarded' }
const STATUS_CLASS: Record<FeedbackStatus, string> = {
  open: 'bg-marine-lt text-marine-dk border-marine/30',
  completed: 'bg-green/15 text-green border-green/40',
  discarded: 'bg-silver-lt text-ink-mid border-silver/60',
}

export function FeedbackStatusBadge({ status }: { status: FeedbackStatus }) {
  return <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold border rounded ${STATUS_CLASS[status]}`}>{STATUS_LABEL[status]}</span>
}

/** What kind of feedback an item is, in the same shape as the status badge so the two sit side by side. */
export function FeedbackCategoryBadge({ category }: { category: FeedbackCategory }) {
  const option = feedbackCategoryOption(category)
  return (
    <span title={option.description} className={`inline-block px-1.5 py-0.5 text-[10px] font-bold border rounded ${FEEDBACK_CATEGORY_CLASS[option.category]}`}>
      {option.label}
    </span>
  )
}

const KIND_LABEL: Record<FeedbackSubmitter['kind'], string> = { operator: 'Operator', facility_administrator: 'Facility administrator', platform_admin: 'SeeWhoCares admin' }

/** "Casey Morgan" failing that the email, failing that the role - whichever the credential had on file. */
export function submitterName(submitter: FeedbackSubmitter): string {
  const fromParts = `${submitter.firstName ?? ''} ${submitter.lastName ?? ''}`.trim()
  return fromParts || (submitter.name ?? submitter.email ?? KIND_LABEL[submitter.kind])
}

/** The login behind the name, when it adds something: an admin's username, or a client person's email. Null when the name shown is already it. */
export function submitterLogin(submitter: FeedbackSubmitter): string | null {
  const login = submitter.username ?? submitter.email ?? null
  return login && login !== submitterName(submitter) ? login : null
}

export function submitterKind(submitter: FeedbackSubmitter): string {
  return KIND_LABEL[submitter.kind]
}

export function formatWhen(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** One line saying how an item ended: who, when, and the reason if it was discarded. */
export function ResolutionLine({ item }: { item: FeedbackItem }) {
  if (!item.resolution) {
    return null
  }
  const verb = item.resolution.outcome === 'completed' ? 'Completed' : 'Discarded'
  return (
    <div className="text-[11px] text-ink-mid">
      {verb} by <span className="font-semibold text-ink" title={item.resolution.by}>{item.resolution.byName ?? item.resolution.by}</span> on {formatWhen(item.resolution.at)}
      {item.resolution.reason && (
        <>
          {' '}
          · <span className="italic">{item.resolution.reason}</span>
        </>
      )}
    </div>
  )
}
